import { Service, ServiceCategory, Business } from '@turizi/shared';

export interface ServiceProviderGroup {
    providerId: string;
    provider: Business | null;
    services: Service[];
}

export interface ServiceCityGroup {
    city: string;
    providers: ServiceProviderGroup[];
}

export interface ServiceCategoryGroup {
    category: string;
    cities: ServiceCityGroup[];
}

/**
 * Groups services by category and then by provider, avoiding duplicates.
 * Services with multiple providers will appear only once under the highest-ranked provider.
 * Grouped stay category providers will be categorized by city.
 */
export function groupServicesByCategoryAndProvider(
    services: Service[],
    categories?: ServiceCategory[]
): ServiceCategoryGroup[] {
    // First group services by category
    const categoryMap: Record<string, Service[]> = {};

    services.forEach(service => {
        const category = service.category;
        if (!categoryMap[category]) {
            categoryMap[category] = [];
        }
        categoryMap[category].push(service);
    });

    // Determine which categories to iterate over
    const categoriesToProcess = categories
        ? categories.filter(cat => categoryMap[cat.category])
        : Object.keys(categoryMap).map(cat => ({ category: cat }));

    // For each category, group by provider
    return categoriesToProcess.map(cat => {
        const category = typeof cat === 'string' ? cat : cat.category;
        const categoryServices = categoryMap[category];

        if (category !== 'stay') {
            return {
                category,
                cities: [{
                    city: 'Other',
                    providers: [{
                        providerId: 'all',
                        provider: null,
                        services: categoryServices.sort((a, b) => (a.rank || 0) - (b.rank || 0))
                    }]
                }]
            };
        }

        const providerMap: Record<string, { provider: any, services: Set<number> }> = {};

        // First, collect all providers and track service IDs
        categoryServices.forEach(service => {
            if (service.providers && service.providers.length > 0) {
                service.providers.forEach(provider => {
                    const providerId = provider.id?.toString() || 'unknown';

                    if (!providerMap[providerId]) {
                        providerMap[providerId] = {
                            provider: provider,
                            services: new Set()
                        };
                    }
                    providerMap[providerId].services.add(service.id);
                });
            } else {
                // Services without providers
                if (!providerMap['no-provider']) {
                    providerMap['no-provider'] = {
                        provider: null,
                        services: new Set()
                    };
                }
                providerMap['no-provider'].services.add(service.id);
            }
        });

        // Now convert service IDs back to service objects, sorted by provider rank (ascending)
        const renderedServiceIds = new Set<number>();

        const providers = Object.entries(providerMap)
            .sort(([, a], [, b]) => {
                // Sort by rank ascending (lower rank = higher priority, shown first)
                const rankA = (a.provider as Business)?.tenant_business?.rank;
                const rankB = (b.provider as Business)?.tenant_business?.rank;

                if (rankA === null || rankA === undefined) return 1;
                if (rankB === null || rankB === undefined) return -1;

                return (rankA as number) - (rankB as number);
            })
            .map(([providerId, data]) => {
                // Get actual service objects for this provider, excluding already rendered
                const providerServices = categoryServices
                    .filter(s => data.services.has(s.id) && !renderedServiceIds.has(s.id))
                    .sort((a, b) => (a.rank || 0) - (b.rank || 0));

                // Mark these services as rendered
                providerServices.forEach(s => renderedServiceIds.add(s.id));

                return {
                    providerId,
                    provider: data.provider,
                    services: providerServices,
                };
            })
            .filter(p => p.services.length > 0) // Only include providers with services
            .sort((a, b) => (a.provider?.tenant_business?.rank || 0) - (b.provider?.tenant_business?.rank || 0));

        // Group providers by city
        const cityMap: Record<string, ServiceProviderGroup[]> = {};
        providers.forEach(p => {
            const city = p.provider?.city || 'Other';
            if (!cityMap[city]) {
                cityMap[city] = [];
            }
            cityMap[city].push(p);
        });

        const cities = Object.entries(cityMap).map(([city, cityProviders]) => ({
            city,
            providers: cityProviders
        })).sort((a, b) => {
            if (a.city === 'Other') return 1;
            if (b.city === 'Other') return -1;
            return a.city.localeCompare(b.city);
        });

        return { category, cities };
    });
}
